"use client";

import { c as _c } from "react/compiler-runtime";
import { IconAlertInfoOutline18, IconArrowRotateClockwiseOutline18 } from "nucleo-ui-outline-18";
import { Button } from "@/components/ui/button";
import { RadioGroup } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useOrganizationWallets } from "@/hooks/useOrganizationWallets";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useSimTokenInfo } from "@/hooks/useSimTokenInfo";
import { buildGmailSampleDefaults, evaluateGmailMatch, parseGmailQuery } from "@/lib/workflow/gmailQuery";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useChainInfo } from "../../../_hooks/useChainInfo";
import ArrayTagInput, { type ArrayTagInputHandle } from "../../form-fields/ArrayTagInput";
import TwitterUserSelector from "../../form-fields/TwitterUserSelector";
import DataRow from "../DataRow";
import TxLogFetcher from "../TxLogFetcher";
import { parseEventAbiFromPayload } from "../eventAbiUtils";
import { FallbackForm } from "./FallbackForm";
import { GmailMatchBadge } from "./GmailMatchBadge";
import { ManualTransferForm } from "./ManualTransferForm";
import { type FarcasterReactionType, buildFarcasterReactionPayload, CastRow, ChannelRow, CoinbasePaymentRow, EvmLogRow, ExchangePairRow, FarcasterReactionTypeToggle, FollowerRow, MarketCloseRow, MentionRow, ModeToggle, NewMarketRow, type TransferMode, TransferModeToggle, PolymarketRow, ReactionRow, ShopifyOrderRow, TradeRow, TransferRow, TweetRow, UserRow, NewsHeadlineRow, FlowAlertRow } from "./HistoricalPickerRows";
import { getArchetypeConfig } from "./registry";
import type { TriggerArchetypeProps } from "./types";

/** Human-readable picker label per trigger type */
function getPickerLabel(triggerType: string): string {
  const labels: Record<string, string> = {
    "erc20-receive": "Choose a recent transfer",
    "erc20-send": "Choose a recent transfer",
    "eth-receive": "Choose a recent transfer",
    "eth-send": "Choose a recent transfer",
    "evm-log": "Choose a recent event",
    "farcaster-new-cast": "Choose a recent cast",
    "farcaster-new-follower": "Choose a recent follower",
    "farcaster-new-reaction": "Choose a cast",
    "polymarket-user-bet": "Choose a recent position",
    "polymarket-market-trade": "Choose a recent trade",
    "polymarket-new-market": "Choose a recent market",
    "polymarket-market-close": "Choose a recent market",
    "slack-new-reaction-added": "Choose a recent reaction",
    "slack-new-channel-created": "Choose a recent channel",
    "slack-new-user-added": "Choose a recent user",
    "slack-new-user-mention": "Choose a recent mention",
    "shopify-order-created": "Choose a recent order",
    "shopify-order-paid": "Choose a recent order",
    "coinbase-payment-received": "Choose a recent payment link",
    "gmail-new-email-received": "Choose a recent email",
    "gmail-new-email-matching-search": "Choose a recent email"
  };
  if (TWITTER_TRIGGERS.has(triggerType)) return "Pick a tweet";
  if (EXCHANGE_LISTING_TRIGGERS.has(triggerType)) return "Pick a trading pair";
  return labels[triggerType] ?? "Choose a recent event";
}
const EVM_TRANSFER_TRIGGERS = new Set(["erc20-receive", "erc20-send", "eth-receive", "eth-send"]);
const EVM_LOG_TRIGGERS = new Set(["evm-log"]);
const FARCASTER_CAST_TRIGGERS = new Set(["farcaster-new-cast"]);
const FARCASTER_FOLLOWER_TRIGGERS = new Set(["farcaster-new-follower"]);
const FARCASTER_REACTION_TRIGGERS = new Set(["farcaster-new-reaction"]);
const POLYMARKET_BET_TRIGGERS = new Set(["polymarket-user-bet"]);
const POLYMARKET_TRADE_TRIGGERS = new Set(["polymarket-market-trade"]);
const POLYMARKET_NEW_MARKET_TRIGGERS = new Set(["polymarket-new-market"]);
const POLYMARKET_CLOSE_TRIGGERS = new Set(["polymarket-market-close"]);
const SLACK_REACTION_TRIGGERS = new Set(["slack-new-reaction-added"]);
const SLACK_MENTION_TRIGGERS = new Set(["slack-new-user-mention"]);
const COINBASE_PAYMENT_TRIGGERS = new Set(["coinbase-payment-received"]);
const SHOPIFY_ORDER_TRIGGERS = new Set(["shopify-order-created", "shopify-order-paid"]);
const TWITTER_TRIGGERS = new Set(["x-new-tweet"]);
const EXCHANGE_LISTING_TRIGGERS = new Set(["exchange-listing"]);
const SLACK_CHANNEL_TRIGGERS = new Set(["slack-new-channel-created"]);
const SLACK_USER_TRIGGERS = new Set(["slack-new-user-added"]);
const UW_NEWS_TRIGGERS = new Set(["unusual-whales-market-news"]);
const UW_FLOW_TRIGGERS = new Set(["unusual-whales-options-flow-alerts"]);
const EXCHANGE_OPTIONS = [{
  id: "binance",
  label: "Binance"
}, {
  id: "upbit",
  label: "Upbit"
}, {
  id: "bithumb",
  label: "Bithumb"
}, {
  id: "coinbase",
  label: "Coinbase"
}, {
  id: "revolut",
  label: "Revolut"
}] as const;
function getExchangeLogoUrl(exchangeId: string): string {
  return `https://ai-icon.b3.fun/logo/${exchangeId}`;
}
function ExchangeLogo(t0) {
  const $ = _c(9);
  const {
    exchangeId,
    label
  } = t0;
  const [hasError, setHasError] = useState(false);
  if (hasError) {
    let t1;
    if ($[0] !== label) {
      t1 = label.charAt(0);
      $[0] = label;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <span aria-hidden="true" className="border-ds-border-default bg-ds-neutral-100 text-ds-text-muted dark:bg-ds-neutral-800 text-3xs flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border font-semibold uppercase">{t1}</span>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t1;
  if ($[4] !== exchangeId) {
    t1 = getExchangeLogoUrl(exchangeId);
    $[4] = exchangeId;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setHasError(true);
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t1) {
    t3 = <img src={t1} alt="" onError={t2} className="border-ds-border-default h-5 w-5 flex-shrink-0 rounded-full border bg-white object-cover" />;
    $[7] = t1;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function ExchangeOptionLabel(t0) {
  const $ = _c(8);
  const {
    exchangeId,
    label
  } = t0;
  let t1;
  if ($[0] !== exchangeId || $[1] !== label) {
    t1 = <ExchangeLogo key={exchangeId} exchangeId={exchangeId} label={label} />;
    $[0] = exchangeId;
    $[1] = label;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== label) {
    t2 = <span className="truncate">{label}</span>;
    $[3] = label;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <div className="flex min-w-0 items-center gap-2.5">{t1}{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}

/**
 * HistoricalPicker archetype — browse and select real events as test data.
 *
 * Single data source: each trigger's fetchFallbackData provides the data
 * (on-chain lookups, API calls, RPC queries, etc.).
 */
export function HistoricalPicker({
  triggerType,
  triggerPayload,
  onRun,
  connectorId
}: TriggerArchetypeProps) {
  const [selectedIndex, setSelectedIndex] = useState<string>("0");
  const [mode, setMode] = useState<"recent" | "paste">("recent");
  const [farcasterReactionType, setFarcasterReactionType] = useState<FarcasterReactionType>("like");
  const [transferMode, setTransferMode] = useState<TransferMode>("recent");
  const switchMode = useCallback((newMode: "recent" | "paste") => {
    setMode(newMode);
    onRun(null);
  }, [onRun]);
  const switchTransferMode = useCallback((newMode: TransferMode) => {
    setTransferMode(newMode);
    onRun(null);
  }, [onRun]);

  // Trigger type classification
  const isEvmTransfer = EVM_TRANSFER_TRIGGERS.has(triggerType);
  const isEvmLog = EVM_LOG_TRIGGERS.has(triggerType);
  const isFarcasterCast = FARCASTER_CAST_TRIGGERS.has(triggerType);
  const isFarcasterFollower = FARCASTER_FOLLOWER_TRIGGERS.has(triggerType);
  const isFarcasterReaction = FARCASTER_REACTION_TRIGGERS.has(triggerType);
  const isPolymarketBet = POLYMARKET_BET_TRIGGERS.has(triggerType);
  const isPolymarketTrade = POLYMARKET_TRADE_TRIGGERS.has(triggerType);
  const isPolymarketNewMarket = POLYMARKET_NEW_MARKET_TRIGGERS.has(triggerType);
  const isPolymarketClose = POLYMARKET_CLOSE_TRIGGERS.has(triggerType);
  const isSlackReaction = SLACK_REACTION_TRIGGERS.has(triggerType);
  const isSlackMention = SLACK_MENTION_TRIGGERS.has(triggerType);
  const isSlackChannel = SLACK_CHANNEL_TRIGGERS.has(triggerType);
  const isSlackUser = SLACK_USER_TRIGGERS.has(triggerType);
  const isCoinbasePayment = COINBASE_PAYMENT_TRIGGERS.has(triggerType);
  const isShopifyOrder = SHOPIFY_ORDER_TRIGGERS.has(triggerType);
  const isTwitter = TWITTER_TRIGGERS.has(triggerType);
  const isExchangeListing = EXCHANGE_LISTING_TRIGGERS.has(triggerType);
  const isUWNews = UW_NEWS_TRIGGERS.has(triggerType);
  const isUWFlow = UW_FLOW_TRIGGERS.has(triggerType);
  const isGmailMatchingSearch = triggerType === "gmail-new-email-matching-search";
  const gmailParsedQuery = useMemo(() => isGmailMatchingSearch ? parseGmailQuery(triggerPayload?.q as string | undefined ?? "") : null, [isGmailMatchingSearch, triggerPayload?.q]);
  const gmailInitialValues = useMemo(() => {
    if (!gmailParsedQuery) return undefined;
    const defaults = buildGmailSampleDefaults(gmailParsedQuery);
    const out: Record<string, string> = {};
    if (defaults.from) out.from = defaults.from;
    if (defaults.to) out.to = defaults.to;
    if (defaults.subject) out.subject = defaults.subject;
    if (defaults.text) out.text = defaults.text;
    return out;
  }, [gmailParsedQuery]);
  const renderGmailPreview = useCallback((values: Record<string, string>) => {
    if (!gmailParsedQuery) return null;
    const result = evaluateGmailMatch({
      from: values.from,
      to: values.to,
      subject: values.subject,
      text: values.text
    }, gmailParsedQuery);
    return <GmailMatchBadge result={result} />;
  }, [gmailParsedQuery]);
  const evmLogParsed = useMemo(() => isEvmLog ? parseEventAbiFromPayload(triggerPayload?.eventAbi) : {
    eventName: undefined,
    eventAbi: undefined
  }, [isEvmLog, triggerPayload?.eventAbi]);
  const evmLogChainId = isEvmLog && typeof triggerPayload?.chainId === "number" ? triggerPayload.chainId : undefined;

  // Data fetcher state
  const [results, setResults] = useState<Record<string, unknown>[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [twitterUsernames, setTwitterUsernames] = useState<string[]>(() => {
    // Mirror the trigger's payload shape: a configured array stays as a list,
    // a single string becomes a list of one. Multi-account fetches use
    // `(from:u1 OR from:u2 OR ...)` (matches buildTwitterSearchQuery on the
    // backend; see services/b3os-workflow/.../publish_twitterapiio.go).
    const configured = triggerPayload?.username;
    if (Array.isArray(configured)) {
      return configured.filter((u): u is string => typeof u === "string" && u.length > 0);
    }
    return typeof configured === "string" && configured ? [configured] : [];
  });
  const [twitterKeywords, setTwitterKeywords] = useState<string[]>(() => {
    const configured = triggerPayload?.keywords as string[] | undefined;
    return configured ?? [];
  });
  const [twitterFetched, setTwitterFetched] = useState(false);
  const [twitterQuery, setTwitterQuery] = useState("");
  const twitterKeywordsInputRef = useRef<ArrayTagInputHandle | null>(null);
  const [exchangeId, setExchangeId] = useState(() => {
    const configured = triggerPayload?.exchange as string | undefined;
    return configured || "binance";
  });
  const [exchangeFetched, setExchangeFetched] = useState(false);

  // Twitter and Exchange Listing flows are mutually exclusive (separate trigger
  // sets), so a single reset that clears both fetched flags is safe — only the
  // active flow's flag was true to begin with.
  const resetSearch = useCallback(() => {
    setTwitterFetched(false);
    setExchangeFetched(false);
    setResults([]);
    setError(null);
    onRun(null);
  }, [onRun]);
  const config = getArchetypeConfig(triggerType);
  const {
    selectedOrgId
  } = useOrganizations();
  const {
    wallets
  } = useOrganizationWallets(selectedOrgId ?? undefined);

  // Some fetchers (EVM transfers) need wallets, others don't
  const needsWallets = isEvmTransfer;
  const walletsReady = !needsWallets || wallets && wallets.length > 0;

  // Request ID pattern: each loadData call increments this. The fetch only
  // updates state if its captured id still matches the latest. This avoids
  // fighting with React StrictMode / unstable parent refs: multiple concurrent
  // fetches are allowed — only the latest one wins. No AbortErrors thrown.
  const requestIdRef = useRef(0);

  // Controller for the currently in-flight request. When a new loadData()
  // starts, we abort the previous controller so the server can cancel the
  // stale RPC work instead of running it to completion (each call can issue
  // dozens of getLogs requests). Held in a ref so aborting doesn't re-render.
  const inFlightControllerRef = useRef<AbortController | null>(null);

  // Stable fingerprint of the payload. Parents often pass a fresh object on
  // every render (React Flow, Zustand, Ably sync, etc.). Without this, the
  // fetch effect would re-run on every parent render.
  //
  // The replacer tolerates BigInt values (JSON.stringify throws on raw BigInts),
  // which can otherwise collapse the key to "" and freeze the fetch effect
  // across subsequent edits.
  const triggerPayloadKey = useMemo(() => {
    try {
      return JSON.stringify(triggerPayload ?? {}, (_k, v) => typeof v === "bigint" ? `__bigint:${v.toString()}` : v);
    } catch {
      return "";
    }
  }, [triggerPayload]);
  const loadData = useCallback(async () => {
    if (!config.fetchFallbackData || !walletsReady) return;
    const myRequestId = ++requestIdRef.current;

    // Cancel the previous in-flight request (if any) so the server stops
    // doing expensive RPC work for a config we no longer care about. Safe
    // because we do this inside loadData() — no setState is triggered, so
    // there's no risk of a re-render loop from an unstable triggerPayload.
    inFlightControllerRef.current?.abort();

    // 30-second overall timeout — only used to signal the fallback to bail early.
    // The actual state update is gated on requestId, not on this signal.
    const controller = new AbortController();
    inFlightControllerRef.current = controller;
    const timeoutId = setTimeout(() => controller.abort(), 30_000);
    setIsLoading(true);
    setError(null);
    try {
      // For Twitter, inject the search query into the payload context so the
      // fallback fetcher can read it. We use a merged object instead of mutating
      // the prop to stay consistent with React's data flow.
      const fetchPayload = isTwitter && twitterQuery ? {
        ...triggerPayload,
        _twitterQuery: twitterQuery
      } : isExchangeListing ? {
        ...triggerPayload,
        _exchangeId: exchangeId
      } : triggerPayload;
      const items = await config.fetchFallbackData({
        triggerType,
        triggerPayload: fetchPayload,
        wallets: wallets?.map(w => ({
          id: w.id,
          address: w.address
        })) ?? [],
        connectorId,
        signal: controller.signal
      });
      // Stale response — a newer loadData superseded us. Don't touch state.
      if (myRequestId !== requestIdRef.current) return;
      if (items.length > 0) {
        setResults(items);
      } else {
        setError(config.emptyStateMessage ?? "No recent events found.");
      }
    } catch (err) {
      if (myRequestId !== requestIdRef.current) return;
      if (controller.signal.aborted) {
        // The "paste mode" hint only applies to EVM log triggers — other
        // triggers (Slack, Shopify, Farcaster, Polymarket, etc.) don't have
        // a paste-mode fallback.
        setError(isEvmLog ? "Timed out loading events. Try paste mode to replay a specific transaction." : "Timed out loading events. Please try again.");
      } else {
        setError(err instanceof Error ? err.message : "Failed to fetch activity.");
      }
    } finally {
      clearTimeout(timeoutId);
      if (myRequestId === requestIdRef.current) {
        setIsLoading(false);
        inFlightControllerRef.current = null;
      }
    }
  }, [config, triggerType, triggerPayload, wallets, connectorId, walletsReady, isTwitter, twitterQuery, isExchangeListing, exchangeId, isEvmLog]);
  useEffect(() => {
    if (isTwitter && !twitterFetched) {
      setIsLoading(false);
      return; // Twitter: wait for manual fetch
    }
    if (isExchangeListing && !exchangeFetched) {
      setIsLoading(false);
      return;
    }
    if (config.fetchFallbackData && walletsReady) {
      loadData();
    } else if (!config.fetchFallbackData) {
      setIsLoading(false);
      setError("No data source configured for this trigger.");
    } else if (needsWallets && wallets && wallets.length === 0) {
      setIsLoading(false);
      setError("No wallets configured. Add a wallet to test this trigger.");
    }
    // No cleanup on dep change — request ID pattern in loadData makes stale
    // responses no-ops, so concurrent fetches are safe. Aborting here would
    // create a loop if triggerPayload were an unstable reference from the parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadData reads latest via closure; triggerPayloadKey is the stable value-based dep
  }, [walletsReady, config.fetchFallbackData, triggerType, triggerPayloadKey, isTwitter, twitterFetched, twitterQuery, isExchangeListing, exchangeFetched, exchangeId]);

  // On unmount, invalidate any in-flight request so late responses don't touch
  // state, and abort the active controller so the server can free its resources.
  useEffect(() => {
    return () => {
      requestIdRef.current = -1;
      inFlightControllerRef.current?.abort();
      inFlightControllerRef.current = null;
    };
  }, []);

  // Token/chain info for EVM triggers
  const chainId = triggerPayload?.chainId as number | undefined;
  const {
    data: chainInfo
  } = useChainInfo(chainId ?? 0);
  const explorerUrl = chainInfo?.explorers?.[0]?.url?.replace(/\/$/, "");
  const configuredToken = triggerPayload?.tokenAddress as string | undefined;
  const {
    data: tokenInfo
  } = useSimTokenInfo(configuredToken, chainId, !!configuredToken && !!chainId);
  const tokenSymbol = tokenInfo?.tokens?.[0]?.symbol;
  const tokenDecimals = tokenInfo?.tokens?.[0]?.decimals;
  const isReceiveTrigger = triggerType === "erc20-receive" || triggerType === "eth-receive";
  const monitoredAddress = isEvmTransfer ? isReceiveTrigger ? triggerPayload?.toAddress as string | undefined ?? triggerPayload?.walletAddress as string | undefined : triggerPayload?.fromAddress as string | undefined ?? triggerPayload?.walletAddress as string | undefined ?? triggerPayload?.toAddress as string | undefined : undefined;

  // Filter and limit results based on trigger type
  const allResults = useMemo(() => {
    let filtered = results;
    if (isEvmTransfer && configuredToken && results.length) {
      const tokenLower = configuredToken.toLowerCase();
      filtered = results.filter(item => {
        const raw = (item.raw ?? {}) as Record<string, unknown>;
        const addr = String(raw.address ?? "").toLowerCase();
        return !addr || addr === tokenLower;
      });
    }
    if (isTwitter) {
      filtered = filtered.slice(0, 10);
    }
    return filtered;
  }, [results, configuredToken, isEvmTransfer, isTwitter]);
  const hasFilterMismatch = useMemo(() => allResults.length > 0 && allResults[0]._filterMismatch === true, [allResults]);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex("0");
  }, [allResults]);

  // Notify parent of selected payload (covers both result changes and user selection)
  useEffect(() => {
    if (isTwitter && !twitterFetched) {
      onRun(null);
      return;
    }
    if (isExchangeListing && !exchangeFetched) {
      onRun(null);
      return;
    }
    if (isEvmLog && mode !== "recent") return; // paste mode manages its own payload via TxLogFetcher
    const idx = Math.min(Number(selectedIndex), allResults.length - 1);
    const payload = allResults[idx];
    if (!payload) return;
    if (isFarcasterReaction) {
      onRun(buildFarcasterReactionPayload(payload, farcasterReactionType));
      return;
    }
    onRun(payload);
  }, [selectedIndex, allResults, mode, isEvmLog, isFarcasterReaction, farcasterReactionType, onRun, isTwitter, twitterFetched, isExchangeListing, exchangeFetched]);
  if (isTwitter && !twitterFetched && !isLoading) {
    const canFetch = twitterUsernames.length > 0 || twitterKeywords.length > 0;
    return <div className="space-y-4">
        <div className="space-y-3">
          <div className="space-y-2 sm:grid sm:grid-cols-[132px_minmax(0,1fr)] sm:items-center sm:gap-4 sm:space-y-0">
            <span className="field-label block">Username</span>
            <TwitterUserSelector label="" value={twitterUsernames} onChange={v => setTwitterUsernames(v ?? [])} />
          </div>
          <div className="space-y-2 sm:grid sm:grid-cols-[132px_minmax(0,1fr)] sm:items-start sm:gap-4 sm:space-y-0">
            <div className="space-y-0.5">
              <span className="field-label block">Keywords</span>
              <p className="caption-xs">Filter keywords (OR logic)</p>
            </div>
            <div className="space-y-2">
              <ArrayTagInput ref={twitterKeywordsInputRef} value={twitterKeywords} onChange={setTwitterKeywords} placeholder="Add keywords and press Enter" embedTagsInInput caseInsensitiveDedup className="w-full" />
              <Button type="button" variant="white" disabled={!canFetch} onClick={() => {
              const committedKeywords = twitterKeywordsInputRef.current?.flushPendingValue() ?? twitterKeywords;
              const parts: string[] = [];
              const cleanUsers = twitterUsernames.map(u => u.trim()).filter(Boolean);
              if (cleanUsers.length === 1) {
                parts.push(`from:${cleanUsers[0]}`);
              } else if (cleanUsers.length > 1) {
                // Distribute prefix per-item; twitterapi.io misparses `from:(a OR b)` as the operator on literal `(a` plus free text. Mirrors joinOR in publish_twitterapiio.go.
                parts.push(`(${cleanUsers.map(u => `from:${u}`).join(" OR ")})`);
              }
              const filteredKeywords = committedKeywords.map(k => k.trim()).filter(Boolean);
              if (filteredKeywords.length === 1) {
                parts.push(filteredKeywords[0]);
              } else if (filteredKeywords.length > 1) {
                parts.push(`(${filteredKeywords.join(" OR ")})`);
              }
              const query = parts.join(" ");
              setTwitterQuery(query);
              // Setting twitterFetched triggers the auto-fetch useEffect,
              // which calls loadData() once the state update flushes.
              // We don't call loadData() directly to avoid double invocation.
              setTwitterFetched(true);
              setResults([]);
              setError(null);
            }} className="w-full sm:w-auto">
                <IconArrowRotateClockwiseOutline18 className="h-3.5 w-3.5" />
                Fetch Tweets
              </Button>
            </div>
          </div>
        </div>
      </div>;
  }
  if (isExchangeListing && !exchangeFetched && !isLoading) {
    const selectedExchange = EXCHANGE_OPTIONS.find(option => option.id === exchangeId) ?? EXCHANGE_OPTIONS[0];
    return <div className="space-y-4">
        <div className="space-y-2 sm:grid sm:grid-cols-[132px_minmax(0,1fr)] sm:items-center sm:gap-4 sm:space-y-0">
          <span className="field-label block">Exchange</span>
          <Select value={exchangeId} onValueChange={setExchangeId}>
            <SelectTrigger className="border-ds-border-default text-ds-text-primary bg-ds-surface-card h-12 w-full rounded-xl text-sm shadow-none [&>span]:flex [&>span]:min-w-0 [&>span]:items-center [&>span]:pr-2">
              <SelectValue>
                <ExchangeOptionLabel exchangeId={selectedExchange.id} label={selectedExchange.label} />
              </SelectValue>
            </SelectTrigger>
            <SelectContent className="w-[var(--radix-select-trigger-width)]">
              {EXCHANGE_OPTIONS.map(option => <SelectItem key={option.id} value={option.id} className="py-2 text-sm">
                  <ExchangeOptionLabel exchangeId={option.id} label={option.label} />
                </SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button type="button" variant="white" onClick={() => {
        setExchangeFetched(true);
        setResults([]);
        setError(null);
      }} className="w-full">
          <IconArrowRotateClockwiseOutline18 className="h-3.5 w-3.5" />
          Fetch Pairs
        </Button>
      </div>;
  }
  if (isLoading) {
    return <div className="flex items-center justify-center gap-2 py-8">
        <PixelLoader size="sm" color="blue" />
        <span className="text-ds-text-muted text-sm">Loading recent events...</span>
      </div>;
  }

  // Paste mode: show TxLogFetcher regardless of error/results state
  if (isEvmLog && mode === "paste") {
    return <div className="space-y-2">
        <ModeToggle mode={mode} onModeChange={switchMode} />
        <TxLogFetcher chainId={evmLogChainId} eventName={evmLogParsed.eventName} eventAbi={evmLogParsed.eventAbi} onRun={onRun} />
      </div>;
  }
  if (isEvmTransfer && transferMode === "custom") {
    return <div className="space-y-3">
        <TransferModeToggle mode={transferMode} onModeChange={switchTransferMode} />
        <ManualTransferForm triggerType={triggerType} chainId={chainId} configuredToken={configuredToken} tokenSymbol={tokenSymbol} tokenDecimals={tokenDecimals} monitoredAddress={monitoredAddress} onRun={onRun} />
      </div>;
  }
  if (error && allResults.length === 0) {
    if (isEvmTransfer) {
      return <div className="space-y-3">
          <TransferModeToggle mode={transferMode} onModeChange={switchTransferMode} />
          <p className="text-ds-text-muted text-center text-xs">
            No recent transfers found.{" "}
            <button type="button" onClick={() => switchTransferMode("custom")} className="text-ds-text-primary underline">
              Enter a custom amount
            </button>
          </p>
        </div>;
    }
    return <div className="space-y-3">
        {isEvmLog && <ModeToggle mode={mode} onModeChange={switchMode} />}
        <div className="flex flex-col items-center gap-3 rounded-lg bg-amber-50 px-4 py-4 text-center dark:bg-amber-900/20">
          <div className="flex items-center justify-center gap-2">
            <IconAlertInfoOutline18 className="h-3.5 w-3.5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
            <span className="text-xs text-amber-700 dark:text-amber-300">{error}</span>
          </div>
          <div className="flex items-center gap-3">
            <Button type="button" variant="white" size="sm" onClick={loadData} className="gap-1.5">
              <IconArrowRotateClockwiseOutline18 className="h-3 w-3" />
              Refresh
            </Button>
            {(isTwitter || isExchangeListing) && <button type="button" onClick={resetSearch} className="label label-xs hover:text-ds-text-subtle transition-colors dark:hover:text-zinc-300">
                Edit search
              </button>}
          </div>
        </div>
        {config.fallbackFormFields && config.fallbackFormFields.length > 0 ? <FallbackForm fields={config.fallbackFormFields} onRun={onRun} initialValues={isGmailMatchingSearch ? gmailInitialValues : undefined} renderPreview={isGmailMatchingSearch ? renderGmailPreview : undefined} /> : null}
      </div>;
  }
  return <div className="space-y-4">
      {isEvmTransfer && <TransferModeToggle mode={transferMode} onModeChange={switchTransferMode} />}
      {isEvmLog && <ModeToggle mode={mode} onModeChange={switchMode} />}
      {isFarcasterReaction && <FarcasterReactionTypeToggle value={farcasterReactionType} onChange={setFarcasterReactionType} />}
      <div className="flex items-center justify-between">
        <div>
          <span className="field-label">{getPickerLabel(triggerType)}</span>
          {isTwitter && <p className="caption-xs">Showing {allResults.length} most recent</p>}
          {isExchangeListing && <p className="caption-xs">{allResults.length} pairs available</p>}
        </div>
        <div className="flex items-center gap-3">
          {(isTwitter || isExchangeListing) && <button type="button" onClick={resetSearch} className="label label-xs hover:text-ds-text-subtle transition-colors dark:hover:text-zinc-300">
              Edit search
            </button>}
          <button type="button" onClick={loadData} className="label label-xs hover:text-ds-text-subtle flex items-center gap-1 transition-colors dark:hover:text-zinc-300">
            <IconArrowRotateClockwiseOutline18 className="h-3 w-3" />
          </button>
        </div>
      </div>
      {hasFilterMismatch && <div className="flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 dark:bg-amber-900/20">
          <IconAlertInfoOutline18 className="h-3.5 w-3.5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
          <span className="text-2xs text-amber-700 dark:text-amber-300">
            No recent events match your configured filters. Showing all recent events instead.
          </span>
        </div>}
      <RadioGroup value={selectedIndex} onValueChange={setSelectedIndex} className={`gap-0 ${isExchangeListing || isTwitter ? "max-h-64 overflow-y-auto" : ""}`}>
        <div className="divide-ds-neutral-100 dark:divide-ds-neutral-800 divide-y">
          {allResults.map((data, index) => isEvmTransfer ? <TransferRow key={index} data={data} value={String(index)} selected={selectedIndex === String(index)} onSelect={setSelectedIndex} triggerType={triggerType} chainId={chainId} chainName={chainInfo?.name} chainIcon={chainInfo?.iconUrl} tokenSymbol={tokenSymbol} tokenDecimals={tokenDecimals} wallets={wallets} /> : isEvmLog ? <EvmLogRow key={index} data={data} value={String(index)} selected={selectedIndex === String(index)} onSelect={setSelectedIndex} explorerUrl={explorerUrl} /> : isFarcasterCast || isFarcasterReaction ? <CastRow key={index} data={data} value={String(index)} selected={selectedIndex === String(index)} onSelect={setSelectedIndex} /> : isFarcasterFollower ? <FollowerRow key={index} data={data} value={String(index)} selected={selectedIndex === String(index)} onSelect={setSelectedIndex} /> : isPolymarketBet ? <PolymarketRow key={index} data={data} value={String(index)} selected={selectedIndex === String(index)} onSelect={setSelectedIndex} /> : isPolymarketTrade ? <TradeRow key={index} data={data} value={String(index)} selected={selectedIndex === String(index)} onSelect={setSelectedIndex} /> : isPolymarketClose ? <MarketCloseRow key={index} data={data} value={String(index)} selected={selectedIndex === String(index)} onSelect={setSelectedIndex} /> : isPolymarketNewMarket ? <NewMarketRow key={index} data={data} value={String(index)} selected={selectedIndex === String(index)} onSelect={setSelectedIndex} /> : isSlackChannel ? <ChannelRow key={index} data={data} value={String(index)} selected={selectedIndex === String(index)} onSelect={setSelectedIndex} /> : isSlackUser ? <UserRow key={index} data={data} value={String(index)} selected={selectedIndex === String(index)} onSelect={setSelectedIndex} /> : isCoinbasePayment ? <CoinbasePaymentRow key={index} data={data} value={String(index)} selected={selectedIndex === String(index)} onSelect={setSelectedIndex} /> : isShopifyOrder ? <ShopifyOrderRow key={index} data={data} value={String(index)} selected={selectedIndex === String(index)} onSelect={setSelectedIndex} /> : isSlackMention ? <MentionRow key={index} data={data} value={String(index)} selected={selectedIndex === String(index)} onSelect={setSelectedIndex} /> : isSlackReaction ? <ReactionRow key={index} data={data} value={String(index)} selected={selectedIndex === String(index)} onSelect={setSelectedIndex} /> : isTwitter ? <TweetRow key={index} data={data} value={String(index)} selected={selectedIndex === String(index)} onSelect={setSelectedIndex} /> : isExchangeListing ? <ExchangePairRow key={index} data={data} value={String(index)} selected={selectedIndex === String(index)} onSelect={setSelectedIndex} /> : isUWNews ? <NewsHeadlineRow key={index} data={data} value={String(index)} selected={selectedIndex === String(index)} onSelect={setSelectedIndex} /> : isUWFlow ? <FlowAlertRow key={index} data={data} value={String(index)} selected={selectedIndex === String(index)} onSelect={setSelectedIndex} /> : <DataRow key={index} data={data} label={`Event ${index + 1}`} value={String(index)} selected={selectedIndex === String(index)} onSelect={setSelectedIndex} />)}
        </div>
      </RadioGroup>
    </div>;
}